import { useAPI } from '@/services/useAPI.js';
import { getUserFriendlyErrorMessage } from '@/helpers/errorHandlerHelper.js';
import { showNotification } from '@/plugins/notification';
import { groupPosts, groupPostsReply } from '@/helpers/groupPostsHelper';
import { authErrorHandler } from '@/services/errorServices/authHandler.js';
import { triggerSessionExpired } from '@/services/errorServices/sessionExpired';

/**
 * Vuex actions for the "group" (org) module.
 *
 * Fetches/mutates organizations, memberships (invites, join requests, roles,
 * bans), channel follows, and group feed posts via the API, committing
 * results via updates.js mutations and surfacing outcomes through toast
 * notifications. Also hosts the `enhanceOrgPostsWithReplies` helper (below)
 * used by the feed-fetching actions.
 */

/**
 * Fetch replies for each non-reply post in `posts` (similar to the main
 * feed), integrating them into `hasIntegratedReplies`/`integratedReplies`,
 * and drop any standalone reply post that was integrated into a parent.
 * Runs with `opts.concurrency` (default 4) requests in flight at a time;
 * network/HTTP errors for a single post's replies are swallowed and that
 * post is returned unchanged.
 */
async function enhanceOrgPostsWithReplies(posts, opts = {}) {
  const { concurrency = 4 } = opts;
  const integratedReplyIds = new Set();

  // Process concurrency helper
  const mapWithConcurrency = async (tasks, limit, mapper) => {
    const results = [];
    for (let i = 0; i < tasks.length; i += limit) {
      const batch = tasks.slice(i, i + limit);
      const batchResults = await Promise.all(batch.map(mapper));
      results.push(...batchResults);
    }
    return results;
  };

  // Prepare mapping tasks only for non-reply posts; keep replies as-is
  const tasks = posts.map(async (post) => {
    if (post.type === 'reply') return post;
    try {
      const repliesResponse = await fetch(`/api/posts/${post.id}/replies`);
      if (!repliesResponse.ok) {
        // Log 503 and other HTTP errors but don't break the flow
        if (repliesResponse.status === 503) {
          if (import.meta.env.MODE === 'development') {
            console.warn(`[enhanceOrgPostsWithReplies] Service unavailable for post ${post.id} replies (503)`);
          }
        } else {
          console.warn(`[enhanceOrgPostsWithReplies] Failed to fetch replies for post ${post.id}: ${repliesResponse.status}`);
        }
        return post;
      }

      const repliesData = await repliesResponse.json();
      const serverReplies = Array.isArray(repliesData.results) ? repliesData.results.slice().reverse() : [];
      
      if (serverReplies.length > 0) {
        // Mark these reply IDs as integrated so they don't appear standalone
        serverReplies.forEach((reply) => {
          if (reply.id) integratedReplyIds.add(reply.id);
        });
        return {
          ...post,
          hasIntegratedReplies: true,
          integratedReplies: serverReplies,
          totalRepliesCount: post.numReplies || repliesData.total || serverReplies.length,
        };
      }
      return post;
    } catch (error) {
      // Catch network errors, timeouts, and other fetch failures
      if (import.meta.env.MODE === 'development') {
        console.debug(`[enhanceOrgPostsWithReplies] Network error for post ${post.id}:`, error.message || error);
      }
      return post;
    }
  });

  // Execute with concurrency cap
  const enhanced = await mapWithConcurrency(tasks, concurrency, (task) => task);
  // Filter out standalone replies that we integrated into parents
  return enhanced.filter((p) => !(p?.type === 'reply' && integratedReplyIds.has(p.id)));
}

export default {
  /** Fetch the list of organization types; commits only if the response is (or wraps) an array, since error bodies are objects. */
  async fetchOrgTypes({ commit }) {
    const response = await fetch(`/api/organization-types`);
    let data = await response.json();
    // OLD: commit('setOrgTypes', data);
    // FastAPI error bodies ({"status_id": 0, "reason": "..."}) are objects, not
    // arrays — committing them made orgTypes.value?.find() throw in
    // createOrg.vue's mounted hook. Only commit a real array.
    commit('setOrgTypes', Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []));
  },

  /** Fetch an organization by id, unwrapping common response-wrapper shapes (`data`/`organization`/`org`), and commit it. */
  async fetchOrgData({ commit }, id) {
    const response = await fetch(`/api/organizations/${id}`);
    let data = await response.json();
    // Debug: log raw API response to verify shape (remove after fix confirmed)
    // console.log('[fetchOrgData] raw response:', data);
    // Old: commit(data) directly — breaks if API wraps response in a key
    // Unwrap common wrapper patterns so orgInfo.ownerId / orgInfo.id are always at root
    const orgData = data?.data || data?.organization || data?.org || data;
    commit('setOrgData', orgData);
    return response;
  },

  /** Fetch and commit the banned-users list for organization `id`. */
  async fetchBannedUsers({ commit }, id) {
    const response = await fetch(`/api/organizations/${id}/banned-users`);
    let data = await response.json();
    commit('setBannedUsers', data);
    return response;
  },

  /** Reset `orgData` to an empty object (the `id` argument is unused). */
  async clearOrgData({ commit }, id) {
    let data = {};
    commit('setOrgData', data);
  },

  /** Commit `data` as the current org profile directly (no API call despite the name). */
  async fetchCurrentProfileOrg({ commit }, data) {
    commit('setCurrentProfileOrg', data);
  },

  /** Fetch a channel by id and commit its `numFollowers` count. */
  async fetchNumFollowersChannel({ commit }, id) {
    const response = await fetch(`/api/channels/${id}`);
    let data = await response.json();
    commit('setNumFollowersChannel', data.numFollowers);
  },

  /**
   * Fetch the current user's orgs, optionally filtered by `params.search`
   * (an `@`-prefixed value does an exact autocomplete search; a plain value
   * falls back to autocomplete if the initial search yields no results).
   * Commits the array (guarding against non-array error bodies) and tracks
   * whether the search matched anything via `setNoSearchOrgs`.
   */
  async fetchOnlyUserOrgs({ commit }, params = {}) {
    let search = params.search || '';
    const fetchOpts = params.force ? { cache: /** @type {RequestCache} */ ('no-cache') } : {};
    let data;
    if (search.startsWith('@')) {
      const cleanSearch = search.substring(1);
      const queryParams = new URLSearchParams({
        limit: '100',
        search: cleanSearch,
        autocomplete: 'true',
      });
      const response = await fetch(`/api/organizations/my?${queryParams.toString()}`, fetchOpts);
      data = await response.json();
      if (data?.message === "User account is deleted" || data?.message === "User no longer exists" || data?.message === "Unauthorized") {
          triggerSessionExpired();
          return;
        }
      // OLD: commit('setMyOrgList', data.results);
      // FastAPI error bodies have no .results — committing undefined crashed
      // Header's mounted hook (myOrgs.value.filter is not a function).
      commit('setMyOrgList', Array.isArray(data?.results) ? data.results : []);
      if (!data.results || data.results.length === 0) {
        commit('setNoSearchOrgs', search);
      } else {
        commit('setNoSearchOrgs', null);
      }
      return;
    }
    let queryParams = new URLSearchParams({
      limit: '100',
    });
    if (search) {
      queryParams.set('search', search);
    }
    let response = await fetch(`/api/organizations/my?${queryParams.toString()}`, fetchOpts);
    data = await response.json();
    if (data?.message === "User account is deleted" || data?.message === "User no longer exists" || data?.message === "Unauthorized") {
        triggerSessionExpired();
        return;
        }
    if (search && (!data.results || data.results.length === 0)) {
      let fallbackParams = new URLSearchParams({
        limit: '100',
        search: search,
        autocomplete: 'true',
      });
      response = await fetch(`/api/organizations/my?${fallbackParams.toString()}`, fetchOpts);
      data = await response.json();
    }
    // OLD: commit('setMyOrgList', data.results); — see guard note above
    commit('setMyOrgList', Array.isArray(data?.results) ? data.results : []);
    if (search && (!data.results || data.results.length === 0)) {
      commit('setNoSearchOrgs', search);
    } else {
      commit('setNoSearchOrgs', null);
    }
  },

  /**
   * Fetch orgs the current user doesn't belong to, with the same
   * `@`-prefixed/autocomplete-fallback search behavior as `fetchOnlyUserOrgs`.
   * Commits the array (guarding against non-array error bodies) and tracks
   * whether the search matched anything via `setNoSearchOrgs`.
   */
  async fetchOtherOrgs({ commit }, params = {}) {
    let search = params.search || '';
    const fetchOpts = params.force ? { cache: /** @type {RequestCache} */ ('no-cache') } : {};
    let data;
    if (search.startsWith('@')) {
      const cleanSearch = search.substring(1);
      const queryParams = new URLSearchParams({
        limit: '100',
        search: cleanSearch,
        autocomplete: 'true',
      });
      const response = await fetch(`/api/organizations/other?${queryParams.toString()}`, fetchOpts);
      data = await response.json();
      if (data?.message === "User account is deleted" || data?.message === "User no longer exists" || data?.message === "Unauthorized") {
        triggerSessionExpired();
        return;
        }
      // OLD: commit('setOtherOrgList', data.results); — same guard as myOrgList
      commit('setOtherOrgList', Array.isArray(data?.results) ? data.results : []);
      if (!data.results || data.results.length === 0) {
        commit('setNoSearchOrgs', search);
      } else {
        commit('setNoSearchOrgs', null);
      }
      return;
    }
    let queryParams = new URLSearchParams({
      limit: '100',
    });
    if (search) {
      queryParams.set('search', search);
    }
    let response = await fetch(`/api/organizations/other?${queryParams.toString()}`, fetchOpts);
    data = await response.json();
    if (search && (!data.results || data.results.length === 0)) {
      let fallbackParams = new URLSearchParams({
        limit: '100',
        search: search,
        autocomplete: 'true',
      });
      response = await fetch(`/api/organizations/other?${fallbackParams.toString()}`, fetchOpts);
      data = await response.json();
    }
    // OLD: commit('setOtherOrgList', data.results); — same guard as myOrgList
    commit('setOtherOrgList', Array.isArray(data?.results) ? data.results : []);
    if (search && (!data.results || data.results.length === 0)) {
      commit('setNoSearchOrgs', search);
    } else {
      commit('setNoSearchOrgs', null);
    }
  },

  /** Fetch an organization by its join/invite code; commits it on success or shows an error notification. */
  async fetchProfOrgByCode({ commit }, code) {
    const response = await fetch(`/api/organizations/by-code/${code}`);
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      commit('setOrgData', data);
    }
    return { response, data };
  },

  /**
   * Create a new organization/group via POST, normalizing legacy
   * ref/object-shaped `isPrivate`. Commits the created org on success;
   * shows an error notification for both HTTP-level failures and
   * embedded `{ reason, status_id: 0 }` error bodies.
   */
  async fetchCreateOrg({ commit }, payload) {
    let response = await fetch(`/api/organizations`, {
      method: 'POST',
      body: JSON.stringify({
        // Support both new boolean form and legacy ref/object form
        isPrivate:
          typeof payload.isPrivate === 'boolean'
            ? payload.isPrivate
            : (payload.isPrivate && typeof payload.isPrivate === 'object' && 'value' in payload.isPrivate)
              ? payload.isPrivate.value
              : payload.isPrivate,
        name: payload.name,
        username: payload.username,
        email: payload.email,
        description: payload.description,
        typeId: payload.typeId,
        avatarFileId: payload.avatarFileId,
        terms: payload.terms,
        county: payload.county || undefined,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    // Backend reports failure either via HTTP status or an embedded
    // { reason, status_id: 0 } body (e.g. "Group with this name already exists")
    // — data.statusCode alone missed the status_id/reason shape, so the error
    // was silently swallowed and treated as success.
    if (!response.ok || data.statusCode || data.status_id === 0) {
      showNotification('error', `Error`, data.reason || data.message, 7000);
    } else {
      commit('setOrgData', data);
    }
    return { response, data };
  },

  /**
   * Update an organization via PATCH, sending only the fields present on
   * `payload` (others become `undefined` and are dropped). Does not commit
   * the response; shows a create- or update-specific success notification
   * based on `payload.type`, or an error notification on failure.
   */
  async fetchUpdateOrg({ commit }, payload) {
    let response = await fetch(`/api/organizations/${payload.orgId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        // Accept boolean or ref/object (backwards compatibility)
        isPrivate: typeof payload.isPrivate === 'boolean'
          ? payload.isPrivate
          : payload.isPrivate && typeof payload.isPrivate === 'object' && 'value' in payload.isPrivate
            ? payload.isPrivate.value
            : undefined,
        isProfessional: typeof payload.isProfessional === 'boolean' ? payload.isProfessional : undefined,
        name: payload.name ? payload.name : undefined,
        username: payload.username ? payload.username : undefined,
        email: payload.email ? payload.email : undefined,
        description: payload.description ? payload.description : undefined,
        typeId: payload.typeId ? payload.typeId : undefined,
        avatarFileId: payload.avatarFileId ? payload.avatarFileId : undefined,
        terms: payload.terms ? payload.terms : undefined,
        county: payload.county ? payload.county : undefined,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      if (payload.type !== 'edit') {
        showNotification('success', `Success`, 'Group has been created', 7000);
      } else {
        showNotification('success', `Success`, 'Group has been updated', 7000);
      }
    }
    return response;
  },

  /**
   * Invite a user to the org by `userId`/`role`. Guards against a missing
   * `payload.orgId` (shows an error and returns a synthetic 400 Response
   * instead of calling the API). Commits the updated org data and shows a
   * success/error notification, handling both HTTP-level and embedded
   * `status_id: 0` failures.
   */
  async fetchAddUser({ commit }, payload) {
    if (!payload?.orgId) {
      showNotification('error', `Error`, 'Group id is missing. Please reopen the group and try again.', 7000);
      return new Response(JSON.stringify({ message: 'Group id is missing' }), {
        status: 400,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    const role = typeof payload.role === 'object' ? payload.role?.value : payload.role;
    let response = await fetch(`/api/organizations/${payload.orgId}/invite-member`, {
      method: 'POST',
      body: JSON.stringify({
        userId: payload.userId,
        role,
        hidden: payload.hidden,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    // Backend reports failure either via HTTP status or an embedded
    // { reason, status_id: 0 } body — data.statusCode alone misses that shape.
    if (!response.ok || data.statusCode || data.status_id === 0) {
      showNotification('error', `Error`, data.reason || data.message, 7000);
    } else {
      commit('setOrgData', data);
      showNotification('success', `Success`, 'Member has been added.', 7000);
    }
    return response;
  },

  /**
   * Invite several people to a group by email in one request, used by
   * InviteMembersModal.vue for both tabs. `payload.isExistingUsers` is true for
   * the "Current Users" tab (app accounts) and false for "New Users".
   * `payload.emails` is `[{ email, role, hidden }]`; `role` may be a
   * ROLE_OPTIONS object or a plain value. Shows an error notification on
   * failure (HTTP-level or embedded `status_id: 0`); the modal shows success.
   * Returns `{ ok, data }` — `ok` is false for an embedded `status_id: 0` even
   * when the HTTP status is 200, so the modal doesn't also show a success toast.
   */
  async fetchInviteMembersByEmail({ commit }, payload) {
    if (!payload?.orgId) {
      showNotification('error', `Error`, 'Group id is missing. Please reopen the group and try again.', 7000);
      // return new Response(JSON.stringify({ message: 'Group id is missing' }), {
      //   status: 400,
      //   headers: { 'Content-Type': 'application/json' },
      // });
      return { ok: false, data: { message: 'Group id is missing' } };
    }

    let response = await fetch(`/api/organizations/${payload.orgId}/invite-members-by-emails`, {
      method: 'POST',
      body: JSON.stringify({
        is_existing_users: !!payload.isExistingUsers,
        emails: (payload.emails || []).map((item) => ({
          email: item.email,
          role: typeof item.role === 'object' ? item.role?.value : item.role,
          hidden: !!item.hidden,
        })),
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json().catch(() => ({}));
    const ok = response.ok && !data.statusCode && data.status_id !== 0;
    if (!ok) {
      showNotification('error', `Error`, data.reason || data.message || 'Invites could not be sent.', 7000);
    } else if (data?.id) {
      // Only refresh the group when the response is the updated org object.
      commit('setOrgData', data);
    }
    // return response;
    return { ok, data };
  },

  /**
   * Edit a member's role/hidden flag. Commits the updated org data and
   * shows a role-changed or profile-visibility-changed success notification
   * depending on `payload.type`, or an error notification on failure
   * (including embedded `status_id: 0` bodies).
   */
  async fetchUpdateUser({ commit }, payload) {
    let response = await fetch(`/api/organizations/${payload.orgId}/edit-member`, {
      method: 'POST',
      body: JSON.stringify({
        userId: payload.userId,
        role: payload.role,
        hidden: payload.hidden,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    // Backend reports failure either via HTTP status or an embedded
    // { reason, status_id: 0 } body (e.g. "Only owner or admin can edit member
    // roles") — data.statusCode alone missed that shape and let the failure
    // fall through to the success branch below.
    if (!response.ok || data.statusCode || data.status_id === 0) {
      showNotification('error', `Error`, data.reason || data.message, 7000);
    } else {
      commit('setOrgData', data);
      if (payload.type === 'display') {
        showNotification(
          'success',
          `Profile visibility`,
          "This member's role visibility on their profile has been successfully updated.",
          7000,
        );
      } else {
        showNotification('success', `Role updated`, "This member's role has been successfully changed.", 7000);
      }
    }
    return response;
  },

  /** Accept a pending org invitation; shows a success/error notification. */
  async fetchAcceptInvite({ commit }, id) {
    let response = await fetch(`/api/organizations/${id}/accept-invitation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification('success', `Success`, 'Member has been added.', 7000);
    }
    return response;
  },

  /** Decline a pending org invitation; shows a success/error notification. */
  async fetchDeclineInvite({ commit }, id) {
    let response = await fetch(`/api/organizations/${id}/decline-invitation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification('success', `Success`, 'Invitation has been declined.', 7000);
    }
    return response;
  },

  /** Cancel an outstanding invite for `payload.userId`; shows a success/error notification. */
  async fetchCancelInvite({ commit }, payload) {
    let response = await fetch(`/api/organizations/${payload.id}/cancel-invitation`, {
      method: 'POST',
      body: JSON.stringify({
        userId: payload.userId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification('success', `Success`, 'Invitation has been canceled.', 7000);
    }
    return response;
  },

  /** Remove `payload.userId` from the org; shows a success/error notification. */
  async fetchDeleteMember({ commit }, payload) {
    let response = await fetch(`/api/organizations/${payload.id}/remove-member`, {
      method: 'POST',
      body: JSON.stringify({
        userId: payload.userId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification('success', `Success`, 'Member has been deleted.', 7000);
    }
    return response;
  },

  /** Ban `payload.userId` from the org; shows a success/error notification. */
  async fetchBanMember({ commit }, payload) {
    let response = await fetch(`/api/organizations/${payload.id}/ban`, {
      method: 'POST',
      body: JSON.stringify({
        userId: payload.userId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification('success', `Success`, 'Member has been banned.', 7000);
    }
    return response;
  },

  /** Unban `payload.userId` from the org; shows a success/error notification. */
  async fetchUnbannedMember({ commit }, payload) {
    let response = await fetch(`/api/organizations/${payload.id}/unban`, {
      method: 'POST',
      body: JSON.stringify({
        userId: payload.userId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification('success', `Success`, 'Member has been unbanned.', 7000);
    }
    return response;
  },

  /**
   * Search users to add/invite to a group. An `@`-prefixed query does an
   * exact autocomplete search; a plain query searches `/api/users` first,
   * falling back to full-text `/api/search`, and retries with `@`-style
   * autocomplete if still empty. Deduplicates results by id and commits
   * them (or an empty result plus the no-match query) via `setSearchMembers`
   * / `setNoSearchMembers`; also clears both on error.
   */
  async fetchSearchMembers({ commit }, query) {
    if (query.trim().length < 2 && query !== '') {
      commit('setSearchMembers', []);
      commit('setNoSearchMembers', null);
      return;
    }
    try {
      let users = [];
      let originalQuery = query;
      if (query.startsWith('@')) {
        const cleanQuery = query.substring(1);
        const userParams = new URLSearchParams({ search: cleanQuery, autocomplete: 'true' });
        if (!cleanQuery || cleanQuery === '') {
          return;
        }
        const userResponse = await fetch(`/api/users?${userParams}`);
        const userData = await userResponse.json();
        users = Array.isArray(userData) ? userData : userData.results || [];
      } else {
        // OLD (bug: search-index `render` objects don't include avatarFile, so
        // every result showed the default avatar image):
        // const params = new URLSearchParams({ query });
        // const response = await fetch(`/api/search?${params}`);
        // const data = await response.json();
        // users = Array.isArray(data) ? data.filter(item => item.collection === 'users').map(item => item.render) : [];
        // NEW: primary lookup via /api/users?search= — returns full user docs with
        // avatarFile (same source the global header search uses for its user results)
        const userParams = new URLSearchParams({ search: query });
        const userResponse = await fetch(`/api/users?${userParams}`);
        const userData = await userResponse.json();
        users = Array.isArray(userData) ? userData : userData.results || [];
        // Keep /api/search as a fallback so full-text matches (name/bio) still surface
        if (!users.length) {
          const params = new URLSearchParams({ query });
          const response = await fetch(`/api/search?${params}`);
          const data = await response.json();
          users = Array.isArray(data) ? data.filter(item => item.collection === 'users').map(item => item.render) : [];
        }
      }
      // Если ничего не найдено, пробуем с @
      if (!users.length && !query.startsWith('@')) {
        const cleanQuery = query;
        const userParams = new URLSearchParams({ search: cleanQuery, autocomplete: 'true' });
        const userResponse = await fetch(`/api/users?${userParams}`);
        const userData = await userResponse.json();
        users = Array.isArray(userData) ? userData : userData.results || [];
      }
      // Filter unique users by id
      const uniqueUsers = [];
      const seenIds = new Set();
      for (const user of users) {
        if (!seenIds.has(user.id)) {
          uniqueUsers.push(user);
          seenIds.add(user.id);
        }
      }
      commit('setSearchMembers', uniqueUsers);
      if (!uniqueUsers.length) {
        commit('setNoSearchMembers', originalQuery);
      } else {
        commit('setNoSearchMembers', null);
      }
    } catch (error) {
      const friendlyMessage = getUserFriendlyErrorMessage(error);
      console.error('Error fetching search results:', friendlyMessage);
      commit('setSearchMembers', []);
      commit('setNoSearchMembers', null);
      
      // In development, also log the original error for debugging
      if (import.meta.env.MODE === 'development') {
        console.error('Original search error:', error);
      }
    }
  },

  /**
   * Follow or monitor a group's channel (`payload.followType`). Commits
   * `addFollowingChannel`, best-effort syncs the same entry onto the cached
   * `auth.user.followingChannels`, and shows a follow- or
   * monitor-specific success notification (or an error notification).
   */
  async fetchFollowChannel({ commit, rootState }, payload) {
    let response = await fetch(`/api/users/follow-channel`, {
      method: 'POST',
      body: JSON.stringify({
        channelId: payload.channelId,
        followType: payload.followType,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      commit("addFollowingChannel", { channelId: payload.channelId, followType: payload.followType });

      try {
        let user = rootState.auth?.user;
        if (user) {
          if (typeof user === 'string') {
            try { user = JSON.parse(user); } catch { }
          }
          user = user || {};
          user.followingChannels = user.followingChannels || [];
          user.followingChannels.push({ channelId: payload.channelId, followType: payload.followType });
          commit('setUserData', user);
        }
      } catch (err) {
        console.error('Failed to sync auth user followingChannels after follow:', err);
      }
      if (payload.followType === 'follow') {
        showNotification(
          'success',
          `Success`,
          'You are now following this group. Its posts will appear in your Following feed.',
          7000,
        );
      } else {
        showNotification(
          'success',
          `Success`,
          'You are now monitoring this group. Its posts will appear in your Monitoring feed.',
          7000,
        );
      }
    }
    return response;
  },

  /**
   * Unfollow a group's channel. Commits `removeFollowingChannel`,
   * best-effort removes the same entry from the cached
   * `auth.user.followingChannels`, and shows a success/error notification.
   */
  async fetchUnFollowChannel({ commit, rootState }, payload) {
    let response = await fetch(`/api/users/unfollow-channel`, {
      method: 'POST',
      body: JSON.stringify({
        channelId: payload.channelId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      commit("removeFollowingChannel", payload.channelId);
      try {
        let user = rootState.auth?.user;
        if (user) {
          if (typeof user === 'string') {
            try { user = JSON.parse(user); } catch { }
          }
          user = user || {};
          user.followingChannels = (user.followingChannels || []).filter(fc => fc.channelId !== payload.channelId);
          commit('setUserData', user);
        }
      } catch (err) {
        console.error('Failed to sync auth user followingChannels after unfollow:', err);
      }
      showNotification(
        'success',
        `Success`,
        'You have unfollowed this group. Its posts will no longer appear in your feeds.',
        7000,
      );
    }
    return response;
  },

  /** Thin wrapper committing `setCollectionOrg` with the given collection payload. */
  addCollectionOrg({ commit }, { collectionName, data, isReply }) {
    commit('setCollectionOrg', { collectionName, data, isReply });
  },
  /**
   * Load the first page of a group's posts (or, when `type === 'Reply'`,
   * replies) by channel. No-ops if a load is already in progress
   * (`state.isLoading`). Clears `orgPosts` before committing the fetched
   * page, then for non-reply pages enhances it with integrated replies
   * before re-committing. Returns `{ results: [], count: 0 }` on error.
   */
  async fetchOrgPosts({ commit, state }, { id, limit, channelId, type }) {
    // Если загрузка уже идет, не делаем повторный запрос
    if (state.isLoading) {
      return;
    }

    // Сначала устанавливаем признак загрузки
    commit('setStatusLoad', true);

    try {
      // Очищаем коллекцию перед загрузкой
      commit('setOrgPosts', []);

      // Posts belong to the org's channel, not the org itself — channelId
      // (from /api/organizations/{id}) must be used here. Fall back to id
      // only if the org data hasn't surfaced a channelId.
      const params = new URLSearchParams({
        limit: limit || 10,
        channelId: channelId || id,
      }).toString();
      let postType;
      if (type !== 'Reply') {
        postType = '&type=post&type=quote&type=repost';
      } else {
        postType = '&type=reply';
      }
      let response = await fetch(`/api/posts?${params}${postType}`);
      let posts = await response.json();

      if (posts.statusCode) {
        authErrorHandler(posts);
      }

      if (postType === '&type=reply') {
        posts.results = await groupPostsReply(posts.results);
      }
      if (posts.count > 0) {
        // First set the initial posts
        commit('setOrgPosts', posts.results);
        
        // Then enhance with integrated replies (like main feed does)
        if (postType !== '&type=reply') {
          try {
            const enhanced = await enhanceOrgPostsWithReplies(posts.results, { concurrency: 6 });
            if (Array.isArray(enhanced) && enhanced.length) {
              commit('setOrgPosts', enhanced);
            }
          } catch (e) {
            console.debug('enhanceOrgPostsWithReplies failed:', e?.message);
          }
        }
      }

      return posts;
    } catch (error) {
      const friendlyMessage = getUserFriendlyErrorMessage(error);
      console.error('Error fetching group posts:', friendlyMessage);
      
      // In development, also log the original error for debugging
      if (import.meta.env.MODE === 'development') {
        console.error('Original group posts error:', error);
      }
      
      return { results: [], count: 0 };
    } finally {
      // В любом случае снимаем признак загрузки
      commit('setStatusLoad', false);
    }
  },

  /**
   * Fetch the next page of an org's posts using `after`/`lastId` cursor
   * pagination. Only the `orgPosts` tab is supported (throws for any other
   * `payload.tab`); non-reply pages are enhanced with integrated replies
   * before being returned. Does not commit to state — caller applies the result.
   */
  async fetchNextPaginationOrgPosts({ commit }, payload) {
    if (!payload) {
      throw new Error('Payload is missing or invalid');
    }

    // const params = new URLSearchParams({
    //   limit: '15',
    //   channelId: payload.channelId,
    //   after: payload.lastId,
    // });
    const params = new URLSearchParams({
      limit: '10',
      channelId: payload.channelId,
      after: payload.lastId,
    });

    let response;
    let posts;
    try {
      switch (payload?.tab) {
        case 'orgPosts':
          let postOrgType;
          if (payload?.type === 0) {
            postOrgType = '&type=post&type=quote&type=repost';
          } else {
            postOrgType = '&type=reply';
          }
          response = await fetch(`/api/posts?${params}${postOrgType}`);
          posts = await response.json();
          break;
        default:
          throw new Error('Unknown tabId');
      }

      if (posts.statusCode) {
        authErrorHandler(posts);
      }
      
      // Enhance with integrated replies for non-reply posts (like main feed pagination does)
      if (payload.tab === 'orgPosts' && payload.type === 0 && posts.results && posts.results.length > 0) {
        try {
          const enhanced = await enhanceOrgPostsWithReplies(posts.results, { concurrency: 6 });
          if (Array.isArray(enhanced) && enhanced.length) {
            // Update the posts results with enhanced data
            posts.results = enhanced;
          }
        } catch (e) {
          console.debug('enhanceOrgPostsWithReplies failed (pagination):', e?.message);
        }
      }
      
      return posts;
    } catch (error) {
      console.error('Error in setNextPagonationPosts:', error);
      throw new Error('Failed to fetch posts. Please try again later.');
    }
  },

  /** Fetch a page of a group's posts/replies filtered by channel and type; returns the raw result without committing to state. */
  async fetchFilteredOrgPosts({ commit }, payload) {
    const params = new URLSearchParams({
      limit: String(payload?.limit ?? '15'),
      channelId: payload?.channel ? payload.channel.id : String(payload?.id ?? ''),
    }).toString();
    let postType;
    if (payload?.type !== 'reply') {
      postType = '&type=post&type=quote&type=repost';
    } else {
      postType = '&type=reply';
    }
    let response = await fetch(`/api/posts?${params}${postType}`);
    let posts = await response.json();
    if (posts?.statusCode) authErrorHandler(posts);
    return posts;
  },

  /** Delete organization `id`; shows a success/error notification. */
  async fetchDeleteOrg({ commit }, id) {
    let response = await fetch(`/api/organizations/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      showNotification('error', `Error`, data?.message || 'Failed to delete group', 7000);
    } else {
      showNotification('success', `Success`, 'The group has been successfully deleted.', 7000);
    }
    return response;
  },

  /** Fetch and commit pending join requests for org `id`; no-ops if `id` is falsy (e.g. org data failed to load). */
  async fetchJoinRequests({ commit }, id) {
    // Callers read this id off orgInfo/channelData after fetchOrgData resolves —
    // if that fetch failed or returned an error-shaped body, the id is missing
    // and this would otherwise call /api/organizations/undefined/join-requests.
    if (!id) return;
    let response = await fetch(`/api/organizations/${id}/join-requests`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      commit('setJoinRequests', data);
    }
  },

  // payload: string id OR { id, skipToast } — skipToast used by public group join (component fires its own toast)
  /**
   * Send a join request for an org. `payload` may be a bare id or
   * `{ id, skipToast }`; `skipToast` suppresses the default success toast
   * for callers (e.g. the public group join flow) that show their own.
   */
  async fetchRequestToJoin({ commit }, payload) {
    const id = typeof payload === 'object' ? payload.id : payload;
    const skipToast = typeof payload === 'object' ? payload.skipToast : false;
    let response = await fetch(`/api/organizations/${id}/request-to-join`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else if (!skipToast) {
      // OLD: always showed "Request sent" — now only for private group flow
      showNotification('success', `Request sent`, 'Your request to join this group has been submitted.', 7000);
    }
    return response;
  },

  /**
   * Leave org `id`. Shows a success/error notification, and on success
   * best-effort removes the channel from local `followingChannels` and the
   * cached `auth.user.followingChannels`.
   */
  async fetchLeaveOrg({ commit, rootState }, id) {
    let response = await fetch(`/api/organizations/${id}/leave`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification(
        'success',
        `Left group`,
        "You have been removed from the group's member list and no longer have access to member-specific features.",
        7000,
      );
      
      try {
        commit('removeFollowingChannel', id);

        let user = rootState.auth?.user;
        if (user) {
          if (typeof user === 'string') {
            try { user = JSON.parse(user); } catch { }
          }
          user = user || {};
          user.followingChannels = (user.followingChannels || []).filter(fc => fc.channelId !== id);
          commit('setUserData', user);
        }
      } catch (err) {
        console.error('Failed to sync state after leaving org:', err);
      }
    }
    return response;
  },

  /** Approve `payload.userId`'s pending join request for `payload.channelId`; shows a success/error notification. */
  async fetchAcceptJoinRequest({ commit }, payload) {
    let response = await fetch(`/api/organizations/${payload.channelId}/accept-join-request`, {
      method: 'POST',
      body: JSON.stringify({
        userId: payload.userId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification(
        'success',
        `Request approved`,
        "The user has been approved and is now part of the group's member list.",
        7000,
      );
    }
    return response;
  },

  /** Decline `payload.userId`'s pending join request for `payload.channelId`; shows a success/error notification. */
  async fetchDeclineJoinRequest({ commit }, payload) {
    let response = await fetch(`/api/organizations/${payload.channelId}/decline-join-request`, {
      method: 'POST',
      body: JSON.stringify({
        userId: payload.userId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      showNotification('success', `Request declined`, 'The user has been declined.', 7000);
    }
    return response;
  },
};
